import React from "react";
import {
  Skeleton,
  message,
  FloatButton,
  List,
  notification,
  Divider,
} from "antd";
import PageLoader from "./PageLoader";
import { gql, useQuery } from "@apollo/client";
import {
  migrationRunListContent,
  deleteMigrationRun,
} from "./MigrationRunList";
import { Navigate, useParams } from "react-router-dom";
import CreateMigrationUserButton from "./CreateMigrationUserButton";
import { client } from "../App";
import { CreateMigrationRunButton } from "./CreateMigrationRunButton";
import { usePermissions } from "../lib/usePermissions";

export const MIGRATION_QUERY = gql`
  query MigrationQuery($projectId: ID!) {
    project(id: $projectId) {
      id
      createdAt
      updatedAt
      company
      adminUserEmails
      migrations {
        id
        name
        status
        scheduledStartTime
        argoWorkflowId
      }
    }
  }
`;

export const DELETE_MIGRATION_ADMIN_USER_MUTATION = gql`
  mutation deleteProjectAdminUserEmail($projectId: ID!, $email: String!) {
    deleteProjectAdminUserEmail(projectId: $projectId, email: $email) {
      id
      adminUserEmails
    }
  }
`;

function migrationDetailRunList(migrations) {
  return (
    <List
      itemLayout="vertical"
      dataSource={migrations}
      renderItem={(migration: any) => (
        <List.Item
          key={migration.id}
          actions={[
            <a
              data-migration-run-id={migration.id}
              onClick={deleteMigrationRun}
            >
              Delete Migration Run
            </a>,
          ]}
        >
          <List.Item.Meta
            title={
              <a
                href={`/migration-runs/${migration.id}`}
              >{`Migration Run # ${migration.id} - ${migration.name}`}</a>
            }
          />
          {migrationRunListContent(migration)}
        </List.Item>
      )}
    />
  );
}

function projectAdminUsers(project) {
  let adminUserEmails = project.adminUserEmails || [];
  if (adminUserEmails.length == 0) {
    return <CreateMigrationUserButton projectId={project.id} />;
  }
  return (
    <div>
      <List
        size="small"
        itemLayout="horizontal"
        dataSource={adminUserEmails}
        renderItem={(email: string) => (
          <List.Item
            key={email}
            actions={[
              <a
                data-project-id={project.id}
                data-email={email}
                onClick={deleteAdminUser}
              >
                Delete
              </a>,
            ]}
          >
            <List.Item.Meta title={email} />
          </List.Item>
        )}
      />
      <CreateMigrationUserButton projectId={project.id} />
    </div>
  );
}

export async function deleteAdminUser(e) {
  const projectId = e.target.dataset.projectId;
  const email = e.target.dataset.email;
  client
    .mutate({
      mutation: DELETE_MIGRATION_ADMIN_USER_MUTATION,
      variables: { projectId: projectId, email: email },
      refetchQueries: [
        { query: MIGRATION_QUERY, variables: { projectId: projectId } },
      ],
    })
    .then(() => {
      notification["success"]({
        message: `Successfully deleted user ${email} from Migration`,
      });
    })
    .catch((err) => {
      notification["error"]({
        message: `Failed to delete user ${email} from Migration`,
        description: err.message,
      });
    });
}

function MigrationDetails(props: any) {
  const { migration_id } = useParams();
  const { permissions } = usePermissions();
  const { loading, error, data } = useQuery(MIGRATION_QUERY, {
    variables: { projectId: migration_id },
    pollInterval: 5000,
  });

  if (loading) return <PageLoader tip="Loading..." delay={500} />;

  if (error) {
    message.error("Failed to get Migration details");
    return <Skeleton />;
  }

  if (data.project == null) {
    notification["info"]({
      message: "Redirected you to Agent list",
    });
    return <Navigate to="/migrations" replace />;
  }

  const migrations = data.project.migrations
    .slice()
    .sort(function (a, b) {
      return a.id - b.id;
    })
    .reverse();

  return (
    <div>
      <FloatButton.BackTop />
      <h1>
        Migration # {migration_id} - {data.project.company}
      </h1>
      <Divider />
      <h2>Admin Emails</h2>
      {projectAdminUsers(data.project)}
      <Divider />
      {permissions.has("migrations_runs.manage") && (
        <>
          <div>
            <CreateMigrationRunButton
              key="createMigration"
              style={{ float: "right" }}
              migrationSearchVal={data.project.company}
            />
            <h2>Migration Runs</h2>
          </div>
          {migrationDetailRunList(migrations)}
        </>
      )}
    </div>
  );
}

export default MigrationDetails;
